import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet,
    Chip
} from '@mui/joy';
import GradeIcon from '@mui/icons-material/Grade';
import axiosConfig from '../util/axiosConfig';

const TestMarks = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [testMarks, setTestMarks] = useState([]);

    useEffect(() => {
        fetchTestMarks();
    }, []);

    const fetchTestMarks = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/test-marks');
            setTestMarks(response.data.testMarks || []);
        } catch (err) {
            console.error('Error fetching test marks:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load test marks data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading test marks...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (testMarks.length === 0) {
        return (
            <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
                <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="h4" sx={{ fontWeight: 'bold', color: '#6e7072' }}>
                        Test marks data not yet available
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
            <Card variant="outlined">
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <GradeIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                            Test Marks
                        </Typography>
                    </Box>
                    <Sheet
                        variant="outlined"
                        sx={{
                            width: '100%',
                            borderRadius: 'sm',
                            overflow: 'auto',
                            maxHeight: '600px',
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Table
                            aria-label="test marks table"
                            sx={{
                                '& tbody tr:nth-child(even)': {
                                    backgroundColor: '#f9f9f9'
                                },
                                '& tbody tr:hover': {
                                    backgroundColor: '#fff5ed'
                                },
                                '& thead': {
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                },
                                '& thead th': {
                                    backgroundColor: '#eb7a24',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    borderBottom: '2px solid',
                                    borderBottomColor: '#e55125'
                                },
                                '& td, & th': {
                                    padding: '12px',
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }
                            }}
                        >
                            <thead>
                                <tr>
                                    <th>Test</th>
                                    <th>Date</th>
                                    <th>Full Marks</th>
                                    <th>Marks Obtained</th>
                                    <th>Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testMarks.map((mark, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Typography level="body-md">
                                                {mark.testName || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-md">
                                                {mark.testDate || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-md">
                                                {mark.fullMarks || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            {mark.isAbsent ? (
                                                <Chip
                                                    size="sm"
                                                    sx={{
                                                        backgroundColor: '#ff4444',
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    A
                                                </Chip>
                                            ) : (
                                                <Typography level="body-md">
                                                    {mark.marksObtained !== null ? mark.marksObtained : '-'}
                                                </Typography>
                                            )}
                                        </td>
                                        <td>
                                            <Typography level="body-md">
                                                {mark.percentage}%
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </CardContent>
            </Card>
        </Box>
    );
};

export default TestMarks;
